import React from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import EmptyState from '@atlaskit/empty-state';
import { fg } from '@atlaskit/platform-feature-flags';
import { CompactEmptyState } from '@atlassian/team-work';
import { EmptyStateNew } from '@atlassian/teams-common/empty-state-new';

import WorkListEmptyImage from '../../assets/empty-work-state/WorkListEmpty_ptc.svg';
import WorkListEmptyImageOld from '../../assets/empty-work-state/WorkListEmpty_ptc_old.svg';
import SearchMoreWork from '../SearchMoreWork';

interface Props {
	userId?: string;
	userFullName?: string;
	isForTable?: boolean;
	isCurrentUser?: boolean;
}

function EmptyWorkState(props: Props) {
	const { isForTable, userId, userFullName, isCurrentUser } = props;
	const intl = useIntl();

	const renderDescText = () => {
		const descMessage = isCurrentUser
			? messages.noWorkListDescForYou
			: messages.noWorkListPersonDesc;

		return userFullName ? (
			<FormattedMessage
				{...descMessage}
				values={{
					user: userFullName,
				}}
			/>
		) : (
			<FormattedMessage {...messages.noWorkListTeamDesc} />
		);
	};

	if (isForTable) {
		return (
			<EmptyState
				header={intl.formatMessage(messages.noWorkListTitle)}
				description={renderDescText()}
				imageUrl={
					fg('enable_team_profile_visual_refresh') ? WorkListEmptyImage : WorkListEmptyImageOld
				}
				primaryAction={
					userId &&
					userFullName && (
						<SearchMoreWork isInEmptyState userId={userId} userFullName={userFullName} />
					)
				}
			/>
		);
	}

	return fg('enable_team_profile_visual_refresh') ? (
		<EmptyStateNew
			title={intl.formatMessage(messages.noWorkListTitle)}
			description={renderDescText()}
			imageUrl={WorkListEmptyImage}
			actions={
				userId &&
				userFullName && (
					<SearchMoreWork isInEmptyState userId={userId} userFullName={userFullName} />
				)
			}
		/>
	) : (
		<CompactEmptyState
			title={intl.formatMessage(messages.noWorkListTitle)}
			description={renderDescText()}
			imageUrl={WorkListEmptyImageOld}
			actions={
				userId &&
				userFullName && (
					<SearchMoreWork isInEmptyState userId={userId} userFullName={userFullName} />
				)
			}
		/>
	);
}

const messages = defineMessages({
	noWorkListTitle: {
		id: 'ptc-directory.profile-page.work-list.no-work-list.title',
		defaultMessage: 'There is no work to see here',
		description:
			'Title of the message displayed when there is no work associated to the profile that is being viewed',
	},
	noWorkListTeamDesc: {
		id: 'ptc-directory.profile-page.work-list.no-work-list.team.description',
		defaultMessage: 'Things the team created, edited or commented on in the last 90 days.',
		description:
			'Description of the message displayed when there is no work associated to the team profile that is being viewed',
	},
	noWorkListPersonDesc: {
		id: 'ptc-directory.profile-page.work-list.no-work-list.personal.description',
		defaultMessage: 'Things {user} created, edited or commented on in the last 90 days.',
		description:
			'Description of the message displayed when there is no work associated to the person profile that is being viewed',
	},
	noWorkListDescForYou: {
		id: 'ptc-directory.profile-page.work-list.no-work-list.personal.description.you',
		defaultMessage: 'Things you created, edited or commented on in the last 90 days.',
		description:
			'Description of the message displayed when there is no work associated to the current logged-in person profile that is being viewed',
	},
});

export default EmptyWorkState;
